import { useState, useRef } from 'react';
import { Button } from './ui/button';
import { Textarea } from './ui/textarea';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Sparkles, Loader2, CheckCircle2, Camera, Pencil } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Per-anecdote AI assistant for the promoter / co-promoter client form.
// Mirrors the contributor flow: follow-up questions -> AI redact -> review ->
// element suggestions -> integrate -> final review -> AI photos -> refine.
export const AnecdoteAIAssistant = ({ apiBase, withCreds, text, stage, onApplyText, onAddPhotos, testIdPrefix, enablePhotos = true, context = '' }) => {
  // idle | questions_loading | questions | expanding | reviewing | suggesting |
  // elements | integrating | final_review | photos_loading | photos | refine | refining | done | error
  const [flowState, setFlowState] = useState('idle');
  const [questions, setQuestions] = useState([]);
  const [answers, setAnswers] = useState({});
  const [draft, setDraft] = useState('');
  const [editingFinal, setEditingFinal] = useState(false);
  const [elements, setElements] = useState([]);
  const [selectedElements, setSelectedElements] = useState(new Set());
  const [photos, setPhotos] = useState([]);
  const [selectedPhotos, setSelectedPhotos] = useState(new Set());
  const [refineText, setRefineText] = useState('');
  const photosOffered = useRef(false);

  const wordCount = (text || '').trim().split(/\s+/).filter(Boolean).length;
  const draftWords = (draft || '').trim().split(/\s+/).filter(Boolean).length;
  const tid = (suffix) => `${testIdPrefix}-${suffix}`;
  const call = (action, body) =>
    axios.post(`${apiBase}/anecdote-ai/${action}`, { text: draft || text, stage, context, ...body }, { withCredentials: withCreds });

  const start = async () => {
    setDraft('');
    setFlowState('questions_loading');
    try {
      const res = await axios.post(`${apiBase}/anecdote-ai/suggest_questions`, { text, stage, context }, { withCredentials: withCreds });
      const qs = res.data?.questions || [];
      if (qs.length === 0) return runRedact([]);
      setQuestions(qs);
      setAnswers({});
      setFlowState('questions');
    } catch {
      runRedact([]);
    }
  };

  const runRedact = async (qa) => {
    setFlowState('expanding');
    try {
      const res = await axios.post(
        `${apiBase}/anecdote-ai/redact`,
        { text, stage, context, followup_qa: qa },
        { withCredentials: withCreds },
      );
      setDraft(res.data?.text || '');
      setFlowState('reviewing');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error al redactar con IA');
      setFlowState('error');
    }
  };

  const submitAnswers = () => {
    const qa = questions
      .map((q, i) => ({ question: q, answer: (answers[i] || '').trim() }))
      .filter((x) => x.answer);
    runRedact(qa);
  };

  const acceptDraft = async () => {
    onApplyText(draft);
    setFlowState('suggesting');
    try {
      const res = await call('suggest_elements', {});
      const els = res.data?.elements || [];
      if (els.length === 0) return fetchPhotos();
      setElements(els);
      setSelectedElements(new Set(els.map((_, i) => i)));
      setFlowState('elements');
    } catch {
      setFlowState('refine');
    }
  };

  const toggleElement = (idx) => {
    setSelectedElements((prev) => {
      const next = new Set(prev);
      if (next.has(idx)) next.delete(idx); else next.add(idx);
      return next;
    });
  };

  const integrateSelected = async () => {
    const chosen = elements.filter((_, i) => selectedElements.has(i));
    if (chosen.length === 0) return fetchPhotos();
    setFlowState('integrating');
    try {
      const res = await call('integrate', { elements: chosen });
      setDraft(res.data?.text || draft);
      setEditingFinal(false);
      setFlowState('final_review');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error al integrar la información');
      setFlowState('refine');
    }
  };

  const acceptFinal = () => {
    onApplyText(draft);
    fetchPhotos();
  };

  const fetchPhotos = async () => {
    if (!enablePhotos || photosOffered.current) return setFlowState('refine');
    photosOffered.current = true;
    setFlowState('photos_loading');
    try {
      const res = await call('suggest_photos', {});
      const ph = res.data?.photos || [];
      if (ph.length === 0) {
        toast.message('No hemos encontrado imágenes ilustrativas para esta anécdota. Puedes continuar sin fotos.');
        return setFlowState('refine');
      }
      setPhotos(ph);
      setSelectedPhotos(new Set(ph.map((_, i) => i)));
      setFlowState('photos');
    } catch {
      setFlowState('refine');
    }
  };

  const togglePhoto = (idx) => {
    setSelectedPhotos((prev) => {
      const next = new Set(prev);
      if (next.has(idx)) next.delete(idx); else next.add(idx);
      return next;
    });
  };

  const addPhotos = () => {
    const chosen = photos.filter((_, i) => selectedPhotos.has(i));
    if (chosen.length > 0) onAddPhotos(chosen);
    setFlowState('refine');
  };

  const runRefine = async () => {
    if (!refineText.trim()) return;
    setFlowState('refining');
    try {
      const res = await call('integrate', { instruction: refineText.trim() });
      setDraft(res.data?.text || draft);
      setRefineText('');
      setEditingFinal(false);
      setFlowState('final_review');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error al ampliar el contenido');
      setFlowState('refine');
    }
  };

  const finish = () => {
    if (draft) onApplyText(draft);
    setFlowState('done');
  };

  return (
    <div className="space-y-2" data-testid={tid('ai-assistant')}>
      {flowState === 'idle' && (
        <div className="flex items-center gap-2 flex-wrap">
          <Button
            size="sm"
            variant="outline"
            className="gap-1.5 border-purple-300 text-purple-700 hover:bg-purple-50 hover:text-purple-800"
            disabled={wordCount < 10}
            onClick={start}
            data-testid={tid('ai-start-btn')}
          >
            <Sparkles className="w-3.5 h-3.5" /> Mejorar esta anécdota con IA
          </Button>
          {wordCount < 10 && (
            <span className="text-[11px] text-muted-foreground">
              Escribe la anécdota (al menos unas líneas) para poder mejorarla con IA.
            </span>
          )}
        </div>
      )}

      {(flowState === 'questions_loading' || flowState === 'expanding' || flowState === 'suggesting' ||
        flowState === 'integrating' || flowState === 'refining' || flowState === 'photos_loading') && (
        <div className="p-3 bg-purple-50 border border-purple-200 rounded-lg flex items-center gap-3" data-testid={tid('ai-loading')}>
          <Loader2 className="w-4 h-4 text-purple-600 animate-spin flex-shrink-0" />
          <p className="text-xs text-purple-700">
            {flowState === 'questions_loading' && 'La IA está leyendo tu anécdota...'}
            {flowState === 'expanding' && 'Redactando una versión enriquecida de tu anécdota. Puede tardar unos segundos.'}
            {flowState === 'suggesting' && 'Buscando elementos de tu historia sobre los que añadir información interesante...'}
            {(flowState === 'integrating' || flowState === 'refining') && 'Integrando la información en tu texto...'}
            {flowState === 'photos_loading' && 'Generando fotos ilustrativas a partir de tu relato. Puede tardar hasta un minuto.'}
          </p>
        </div>
      )}

      {flowState === 'questions' && (
        <div className="p-3 bg-purple-50 border border-purple-200 rounded-lg space-y-3" data-testid={tid('ai-questions')}>
          <div className="flex items-start gap-2.5">
            <Sparkles className="w-4 h-4 text-purple-600 mt-0.5 flex-shrink-0" />
            <div>
              <p className="text-sm text-purple-800 font-medium">Unas preguntas rápidas antes de redactar</p>
              <p className="text-xs text-purple-600 mt-0.5">
                Responder (aunque sea brevemente) hará que la versión redactada sea mucho más rica. Son opcionales.
              </p>
            </div>
          </div>
          <div className="space-y-2 ml-6">
            {questions.map((q, i) => (
              <div key={i}>
                <Label className="text-[11px] mb-1 block text-purple-800">{q}</Label>
                <Input
                  value={answers[i] || ''}
                  onChange={(e) => setAnswers((prev) => ({ ...prev, [i]: e.target.value }))}
                  className="h-8 text-sm bg-white"
                  data-testid={tid(`ai-question-${i}`)}
                />
              </div>
            ))}
          </div>
          <div className="flex flex-wrap gap-2 ml-6">
            <Button size="sm" className="gap-1.5 bg-purple-600 hover:bg-purple-700 text-white" onClick={submitAnswers} data-testid={tid('ai-answers-btn')}>
              <Sparkles className="w-3 h-3" /> Continuar y redactar
            </Button>
            <Button size="sm" variant="outline" onClick={() => runRedact([])} data-testid={tid('ai-skip-questions-btn')}>
              Omitir preguntas
            </Button>
          </div>
        </div>
      )}

      {(flowState === 'reviewing' || flowState === 'final_review') && (
        <div className="p-3 bg-purple-50 border border-purple-200 rounded-lg space-y-3" data-testid={tid('ai-review')}>
          <div className="flex items-start gap-2.5">
            <Sparkles className="w-4 h-4 text-purple-600 mt-0.5 flex-shrink-0" />
            <div>
              <p className="text-sm text-purple-800 font-medium">
                {flowState === 'reviewing' ? 'Versión redactada lista' : 'Texto final de tu anécdota'}
              </p>
              <p className="text-xs text-purple-600 mt-0.5">
                {flowState === 'reviewing'
                  ? 'Hemos redactado una versión enriquecida. Puedes editarla antes de aceptar.'
                  : 'Este es el texto tras integrar la información. Revísalo; puedes editarlo con el lápiz.'}
              </p>
            </div>
          </div>
          <div className="ml-6 bg-white border rounded-md p-3">
            <div className="flex items-center justify-between mb-2">
              <span className="text-xs text-muted-foreground">{draftWords} palabras</span>
              {flowState === 'final_review' && (
                <button
                  type="button"
                  onClick={() => setEditingFinal((v) => !v)}
                  className="p-1 rounded hover:bg-purple-100 text-purple-500 hover:text-purple-700 transition-colors"
                  title="Editar texto"
                  data-testid={tid('ai-edit-final-btn')}
                >
                  <Pencil className="w-3.5 h-3.5" />
                </button>
              )}
            </div>
            {flowState === 'reviewing' || editingFinal ? (
              <Textarea
                value={draft}
                onChange={(e) => setDraft(e.target.value)}
                className="min-h-[120px] text-sm"
                data-testid={tid('ai-draft-textarea')}
              />
            ) : (
              <p className="text-sm whitespace-pre-wrap leading-relaxed" data-testid={tid('ai-final-text')}>{draft}</p>
            )}
          </div>
          <div className="flex flex-wrap gap-2 ml-6">
            {flowState === 'reviewing' ? (
              <>
                <Button size="sm" className="gap-1.5 bg-purple-600 hover:bg-purple-700 text-white" onClick={acceptDraft} data-testid={tid('ai-accept-draft-btn')}>
                  <CheckCircle2 className="w-3 h-3" /> Aceptar y continuar
                </Button>
                <Button size="sm" variant="outline" onClick={() => { setDraft(''); setFlowState('idle'); }} data-testid={tid('ai-decline-btn')}>
                  Mantener mi texto original
                </Button>
              </>
            ) : (
              <Button size="sm" className="gap-1.5 bg-purple-600 hover:bg-purple-700 text-white" onClick={acceptFinal} data-testid={tid('ai-accept-final-btn')}>
                <CheckCircle2 className="w-3 h-3" /> Aceptar y continuar
              </Button>
            )}
          </div>
        </div>
      )}

      {flowState === 'elements' && (
        <div className="p-3 bg-purple-50 border border-purple-200 rounded-lg space-y-3" data-testid={tid('ai-elements')}>
          <div className="flex items-start gap-2.5">
            <Sparkles className="w-4 h-4 text-purple-600 mt-0.5 flex-shrink-0" />
            <div>
              <p className="text-sm text-purple-800 font-medium">¿Quieres que profundicemos en algún elemento?</p>
              <p className="text-xs text-purple-600 mt-0.5">
                Hemos detectado estos elementos en tu anécdota. Marca los que quieras y añadiremos una breve nota informativa integrada en el texto.
              </p>
            </div>
          </div>
          <div className="ml-6 space-y-2">
            {elements.map((el, i) => (
              <label key={i} className="flex items-start gap-2.5 bg-white border rounded-md p-2.5 cursor-pointer hover:bg-purple-50/50" data-testid={tid(`ai-element-${i}`)}>
                <input
                  type="checkbox"
                  checked={selectedElements.has(i)}
                  onChange={() => toggleElement(i)}
                  className="mt-0.5 accent-purple-600"
                  data-testid={tid(`ai-element-checkbox-${i}`)}
                />
                <span className="text-sm">
                  <strong className="text-purple-800">{el.term}</strong>
                  {el.reason && <span className="text-muted-foreground"> — {el.reason}</span>}
                </span>
              </label>
            ))}
          </div>
          <div className="flex flex-wrap gap-2 ml-6">
            <Button
              size="sm"
              className="gap-1.5 bg-purple-600 hover:bg-purple-700 text-white"
              onClick={integrateSelected}
              disabled={selectedElements.size === 0}
              data-testid={tid('ai-integrate-btn')}
            >
              <Sparkles className="w-3 h-3" /> Añadir información seleccionada
            </Button>
            <Button size="sm" variant="outline" onClick={fetchPhotos} data-testid={tid('ai-skip-elements-btn')}>
              No añadir nada
            </Button>
          </div>
        </div>
      )}

      {flowState === 'photos' && (
        <div className="p-3 bg-purple-50 border border-purple-200 rounded-lg space-y-3" data-testid={tid('ai-photos')}>
          <div className="flex items-start gap-2.5">
            <Sparkles className="w-4 h-4 text-purple-600 mt-0.5 flex-shrink-0" />
            <div>
              <p className="text-sm text-purple-800 font-medium">¿Quieres añadir alguna de estas fotos?</p>
              <p className="text-xs text-purple-600 mt-0.5">
                Son imágenes ilustrativas generadas a partir de tu relato. Marca las que quieras y las añadiremos a la anécdota.
              </p>
            </div>
          </div>
          <div className="ml-6 grid grid-cols-2 sm:grid-cols-3 gap-3">
            {photos.map((ph, i) => (
              <label
                key={i}
                className={`relative block border-2 rounded-lg overflow-hidden cursor-pointer transition-colors ${selectedPhotos.has(i) ? 'border-purple-500' : 'border-stone-200'}`}
                data-testid={tid(`ai-photo-${i}`)}
              >
                <input
                  type="checkbox"
                  className="absolute top-2 left-2 z-10 w-4 h-4 accent-purple-600"
                  checked={selectedPhotos.has(i)}
                  onChange={() => togglePhoto(i)}
                  data-testid={tid(`ai-photo-checkbox-${i}`)}
                />
                <img src={`${API_URL}${ph.url}`} alt={ph.caption || 'Foto sugerida'} className="w-full h-28 object-cover" />
                {ph.caption && <p className="text-[11px] text-muted-foreground p-1.5 bg-white leading-snug">{ph.caption}</p>}
              </label>
            ))}
          </div>
          <div className="flex flex-wrap gap-2 ml-6">
            <Button
              size="sm"
              className="gap-1.5 bg-purple-600 hover:bg-purple-700 text-white"
              onClick={addPhotos}
              disabled={selectedPhotos.size === 0}
              data-testid={tid('ai-add-photos-btn')}
            >
              <Camera className="w-3 h-3" /> Añadir seleccionadas
            </Button>
            <Button size="sm" variant="outline" onClick={() => setFlowState('refine')} data-testid={tid('ai-skip-photos-btn')}>
              No añadir fotos
            </Button>
          </div>
        </div>
      )}

      {flowState === 'refine' && (
        <div className="p-3 bg-purple-50 border border-purple-200 rounded-lg space-y-3" data-testid={tid('ai-refine')}>
          <div className="flex items-start gap-2.5">
            <Sparkles className="w-4 h-4 text-purple-600 mt-0.5 flex-shrink-0" />
            <div>
              <p className="text-sm text-purple-800 font-medium">¿Quieres que ampliemos algo en concreto?</p>
              <p className="text-xs text-purple-600 mt-0.5">
                Dinos qué parte quieres ampliar. Si no, finaliza: el texto ya está guardado en tu anécdota.
              </p>
            </div>
          </div>
          <div className="ml-6 space-y-2">
            <Textarea
              value={refineText}
              onChange={(e) => setRefineText(e.target.value)}
              placeholder='Ej.: "Amplía la parte del huerto" o "añade más contexto sobre los años 80"'
              className="min-h-[60px] text-sm bg-white"
              data-testid={tid('ai-refine-input')}
            />
          </div>
          <div className="flex flex-wrap gap-2 ml-6">
            <Button
              size="sm"
              className="gap-1.5 bg-purple-600 hover:bg-purple-700 text-white"
              onClick={runRefine}
              disabled={!refineText.trim()}
              data-testid={tid('ai-refine-btn')}
            >
              <Sparkles className="w-3 h-3" /> Ampliar con IA
            </Button>
            <Button size="sm" variant="outline" onClick={finish} data-testid={tid('ai-finish-btn')}>
              <CheckCircle2 className="w-3 h-3 mr-1" /> Finalizar
            </Button>
          </div>
        </div>
      )}

      {flowState === 'done' && (
        <div className="p-2.5 bg-emerald-50 border border-emerald-200 rounded-lg flex items-center justify-between gap-2 flex-wrap" data-testid={tid('ai-done')}>
          <p className="text-xs text-emerald-800 flex items-center gap-1.5">
            <CheckCircle2 className="w-3.5 h-3.5 text-emerald-600" /> Anécdota mejorada con IA y guardada.
          </p>
          <Button size="sm" variant="ghost" className="gap-1.5 text-purple-700 h-7" onClick={start} data-testid={tid('ai-restart-btn')}>
            <Sparkles className="w-3 h-3" /> Volver a mejorar
          </Button>
        </div>
      )}

      {flowState === 'error' && (
        <div className="p-2.5 bg-amber-50 border border-amber-200 rounded-lg space-y-2" data-testid={tid('ai-error')}>
          <p className="text-xs text-amber-800">
            No hemos podido generar la versión enriquecida en este momento. Tu texto original sigue intacto.
          </p>
          <Button size="sm" variant="outline" className="gap-1.5" onClick={start} data-testid={tid('ai-retry-btn')}>
            <Sparkles className="w-3 h-3" /> Reintentar con IA
          </Button>
        </div>
      )}
    </div>
  );
};
